import { useEffect } from 'react'
import { connect } from 'react-redux'
import withApiError, {
  ERROR_TYPE_REPORTING,
} from 'layers/errorHandling/apiError'
import PropTypes from 'prop-types'
import { SpinnerAnimatedIcon } from 'secondstep-components'
import {
  operations as reportsOperations,
  selectors as reportsSelectors,
} from 'store/reportsManager'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import { LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL } from 'components/AdminSiteHeader/constants'
import { selectors as licenseSelectors } from 'store/licenseManager'
import AdminMultiSiteView from './component'
import { LoadingWrapper } from './component.styles'

export const AdminMultiSiteViewContainer = ({
  currentPage,
  email,
  fetchLessonCompletionData,
  isFetchingLessons,
  lessonCompletionUnitsLabel,
  orderBy,
  programKey,
  shouldFetchLessons,
  showLessonCompletionExport,
  sites,
  sitesPerPage,
  sitesPreferences,
  sortKey,
  summary,
  totalCount,
  totalPages,
  updateMonitorSelectedPaginationCurrentPage,
  updateMonitorSelectedPaginationSortKey,
  updateMonitorSelectedPaginationSortOrder,
}) => {
  useEffect(() => {
    if (shouldFetchLessons) {
      fetchLessonCompletionData({ adminEmail: email, program: programKey })
    }
  }, [shouldFetchLessons])

  const getPaginatedSites = (page, key, order) => {
    if (key !== sortKey) updateMonitorSelectedPaginationSortKey(key)
    if (order !== orderBy) updateMonitorSelectedPaginationSortOrder(order)
    if (page !== currentPage) updateMonitorSelectedPaginationCurrentPage(page)
  }

  if (isFetchingLessons) {
    return (
      <LoadingWrapper>
        <SpinnerAnimatedIcon />
        <p>Loading...</p>
      </LoadingWrapper>
    )
  }

  if (!Array.isArray(sites) || !summary) {
    return null
  }

  return (
    <AdminMultiSiteView
      currentPage={currentPage}
      getPaginatedSites={getPaginatedSites}
      isFetchingLessons={isFetchingLessons}
      lessonCompletionUnitsLabel={lessonCompletionUnitsLabel}
      orderBy={orderBy}
      showLessonCompletionExport={showLessonCompletionExport}
      sites={sites}
      sitesPerPage={sitesPerPage}
      sitesPreferences={sitesPreferences}
      sortKey={sortKey}
      summary={summary}
      totalCount={totalCount}
      totalPages={totalPages}
    />
  )
}

const mapDispatchToProps = {
  fetchLessonCompletionData: reportsOperations.fetchLessonCompletionData,
  updateMonitorSelectedPaginationCurrentPage:
    reportsOperations.updateMonitorSelectedPaginationCurrentPage,
  updateMonitorSelectedPaginationSortKey:
    reportsOperations.updateMonitorSelectedPaginationSortKey,
  updateMonitorSelectedPaginationSortOrder:
    reportsOperations.updateMonitorSelectedPaginationSortOrder,
}

export const mapStateToProps = state => {
  const {
    reportsManager: {
      admin,
      isFetchingAdminLessonCompletionData: isFetchingLessons,
      error,
      selectedProgram: { key: selectedProgramKey },
    },
    userProfileManager: { profile },
  } = state
  const k8ToggleView = state.userSessionManager?.adminDashboard?.k8?.toggleView
  const { email } = profile
  const sites = reportsSelectors.selectSortedAdminLessonCompletionData(state)
  const lessonCompletionData = reportsSelectors.selectAdminLessonCompletionData(
    state,
  )
  const allLessonSites = lessonCompletionData?.sites ?? []
  const adminK8SitesWithDwb = licenseSelectors.selectActiveAdminK8SitesWithDwb(
    state,
  )
  const dwbSiteIdSet = new Set(adminK8SitesWithDwb.map(s => s.id))
  const isDwbSpecializedMonitor =
    k8ToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS && dwbSiteIdSet.size > 0

  const sitesPreferences = licenseSelectors.selectActiveAdminSitePreferences(
    state,
  )
  const summary = reportsSelectors.selectAdminLessonCompletionSummaryData(state)

  const { monitorPagination } = admin
  const {
    currentPage,
    sitesPerPage,
    totalCount,
    totalPages,
    sortKey,
    orderBy,
  } = monitorPagination

  const dwbFilteredSiteCount = isDwbSpecializedMonitor
    ? allLessonSites.filter(s => dwbSiteIdSet.has(s.siteId)).length
    : totalCount

  const effectiveTotalCount = isDwbSpecializedMonitor
    ? dwbFilteredSiteCount
    : totalCount

  const effectiveTotalPages =
    isDwbSpecializedMonitor && sitesPerPage > 0
      ? Math.max(1, Math.ceil(dwbFilteredSiteCount / sitesPerPage))
      : totalPages

  const summaryForView =
    isDwbSpecializedMonitor && summary
      ? { ...summary, sitesTotal: dwbFilteredSiteCount }
      : summary

  const shouldFetchLessons = !sites && !summary && !isFetchingLessons && !error

  const lessonCompletionUnitsLabel =
    k8ToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS
      ? LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL
      : 'Core Units'

  const showLessonCompletionExport =
    k8ToggleView !== TOGGLE_VIEW.SPECIALIZED_UNITS

  return {
    currentPage,
    email,
    isFetchingLessons,
    lessonCompletionUnitsLabel,
    orderBy,
    programKey: selectedProgramKey,
    showLessonCompletionExport,
    shouldFetchLessons,
    sites,
    sitesPerPage,
    sitesPreferences,
    sortKey,
    summary: summaryForView,
    totalCount: effectiveTotalCount,
    totalPages: effectiveTotalPages,
  }
}

AdminMultiSiteViewContainer.propTypes = {
  currentPage: PropTypes.number,
  email: PropTypes.string,
  fetchLessonCompletionData: PropTypes.func,
  isFetchingLessons: PropTypes.bool,
  lessonCompletionUnitsLabel: PropTypes.string.isRequired,
  orderBy: PropTypes.string,
  programKey: PropTypes.string,
  shouldFetchLessons: PropTypes.bool,
  showLessonCompletionExport: PropTypes.bool,
  sites: PropTypes.array,
  sitesPerPage: PropTypes.number,
  sitesPreferences: PropTypes.object,
  sortKey: PropTypes.string,
  summary: PropTypes.object,
  totalCount: PropTypes.number,
  totalPages: PropTypes.number,
  updateMonitorSelectedPaginationCurrentPage: PropTypes.func,
  updateMonitorSelectedPaginationSortKey: PropTypes.func,
  updateMonitorSelectedPaginationSortOrder: PropTypes.func,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withApiError(AdminMultiSiteViewContainer, [ERROR_TYPE_REPORTING]))
